// The ranged attack, staged (issue 1527; docs/MRCS/shadowrun-3.md §5.5): the form behind "Ranged attack…" in the dice
// bag, only where the table's system names a staged attack (so a d20 table never sees it), and the defender's question.
// From a weapon row of the attacker's sheet (its damage code and its range bands from the weapon register), or one typed,
// the attacker picks a target; the distance gives the band (which the attacker may change); the TN is shown built from
// the band, the situations ticked, the visibility for the attacker's vision and the attacker's wound; pool dice are capped
// at the skill. The browser names all of that and nothing the server decides: the server throws, asks the defender, and
// stages the damage. The defender's player (or the DM) answers with pool dice; the DM may give a rating the sheet lacks.
//
// Melee (issue 1528; §5.6): the same form behind "Melee attack…", with no band: each side's reach (the weapon's and the
// natural) and which way the longer counts, each side's own situations and vision, a typed weapon's Power from the
// wielder's Strength. The defender (or the DM) answers with their melee skill, their weapon and pool dice (MeleeAsk),
// and the one hit with resistance dice — the attacker's player, when the defender is the one who hits.
import { useEffect, useMemo, useState } from 'react';
import {
  STAGED_REQUEST_LIMITS, STAGED_RECOIL, FIRING_LIMITS, addsDice, bandAt, drawsFor, fireOf, fireRounds, firedNow, parseCodeText, parseFiring, poolAsk, poolCap, stagedAsked, tokenDistance, tokenLabel, woundsOf,
  type RollRecord, type SeenPool, type StagedWeapon, type TestChoice, type Values,
} from '@dragons-table/vtt-shared';
import { selection } from '../../board/selection';
import { fetchTests, type SheetWeaponRow, type TestsAnswer } from '../../entries';
import { useTableState } from '../../table/TableState';
import { useWoundRules } from '../../table/numberWords';
import { useShell } from '../context';

// A disabled button looks it (issue 1548): dimmed, dashed and not a hand, never like a number box.
const btn = 'rounded border border-stone-600 px-2 py-0.5 text-xs hover:bg-stone-800 disabled:cursor-not-allowed disabled:border-dashed disabled:opacity-50 disabled:hover:bg-transparent';
const field = 'rounded bg-stone-800 px-1 py-0.5 text-xs';

interface TokenLike { id: string; kind: string; name?: string; mine?: boolean; character?: string | null; x: number; y: number; values?: Values; /** Its astral state (issue 1531), as its owner and the DM are sent it. */ astral?: string; /** The entry it is linked to (issue 1534: a creature's), as this person is sent it. */ entry?: { name: string } }
interface Fighting { tokens: string[]; pools?: Record<string, SeenPool>; fired?: { turn: string; points: number } }

const TYPED = 'typed';
const int = (v: string): number | null => (v.trim() !== '' && Number.isInteger(Number(v)) ? Number(v) : null);
const signed = (n: number): string => (n < 0 ? `− ${-n}` : `+ ${n}`);
/** "a", "a and b", "a, b and c". */
const and = (xs: readonly string[]): string => (xs.length < 2 ? xs.join('') : `${xs.slice(0, -1).join(', ')} and ${xs.at(-1)}`);

/** The skill a weapon's type names ("Heavy Pistols" fires with Pistols), by the words the two share; else the first. */
export function skillFor(subtype: string | undefined, choices: readonly TestChoice[]): number {
  if (!subtype) return 0;
  const words = subtype.toLowerCase().split(/[^a-z]+/).filter(Boolean);
  const i = choices.findIndex((c) => c.kind === 'skill' && c.name.toLowerCase().split(/[^a-z]+/).some((w) => w.length > 3 && words.includes(w)));
  return i < 0 ? 0 : i;
}

/**
 * What an attack or a melee defence may be thrown with: a sheet's skills and specializations; for a creature linked to
 * its entry (issue 1534), the attributes its system names, its attacks' first.
 */
const attackChoices = (got: Exclude<TestsAnswer, null> | null): TestChoice[] =>
  (got?.choices ?? []).filter((c) => c.kind === 'skill' || c.kind === 'specialization' || (got?.from === 'entry' && c.kind === 'attribute'));

/** A sheet's natural reach (§3.4 `reach`, a troll's 1), from the numbers this person is sent; 0 when none. */
const naturalReach = (v: Values | undefined): number => {
  const n = v?.sheet?.find((x) => x.purpose === 'reach');
  return n && Number.isInteger(n.value) && n.value >= 0 && n.value <= STAGED_REQUEST_LIMITS.reach ? n.value : 0;
};
const halfIn = (melee: boolean, mode: 'half' | 'full' | undefined, n: number): number => (melee && mode === 'half' ? Math.floor(n / 2) : n);
// Whether a typed weapon may add the wielder's rating (`codes.wielder`): only a token whose sheet gives it. A token with
// no sheet, a sheet that could not be read, or one the server says lacks it (`melee.wielder`) types the whole Power.
const readsWielder = (o: { character?: string | null; entry?: unknown } | undefined, answer: TestsAnswer | 'loading'): boolean =>
  Boolean(o?.character || o?.entry) && answer !== null && (answer === 'loading' || !answer.unread?.includes('melee.wielder'));

export function StagedForm({ isDm, priv, onClose, melee = false }: { isDm: boolean; priv: boolean; onClose: () => void; /** The melee attack (issue 1528). */ melee?: boolean }) {
  const { store, act, setNotice, systemRules, scene } = useTableState();
  const { campaignId } = useShell();
  const view = systemRules.staged;
  const meleeRule = melee ? view?.melee ?? null : null;
  // The wielder's rating a typed weapon's Power may add (`codes.wielder`), by the data's own word.
  const wielder = view?.codes.wielder?.label ?? null;
  const kinds = systemRules.damageKinds ?? [];
  const tokens = Object.values(store.state.objects as Record<string, TokenLike>).filter((o) => o.kind === 'token');
  const mine = tokens.filter((o) => (isDm ? true : o.mine));
  const picked = [...selection.get()].find((id) => mine.some((o) => o.id === id));
  const [who, setWho] = useState(picked ?? mine[0]?.id ?? '');
  const [whom, setWhom] = useState('');
  const [answer, setAnswer] = useState<TestsAnswer | 'loading'>(null);
  const [weapon, setWeapon] = useState(TYPED);
  const [typedName, setTypedName] = useState('');
  const [typedPower, setTypedPower] = useState('6');
  const [typedLevel, setTypedLevel] = useState(view?.codes.levels[1]?.key ?? view?.codes.levels[0]?.key ?? '');
  const [typedKind, setTypedKind] = useState(kinds[0]?.key ?? '');
  const [skill, setSkill] = useState('0');
  const [typedSkill, setTypedSkill] = useState('');
  const [typedRating, setTypedRating] = useState('4');
  const [band, setBand] = useState('');
  const [ticked, setTicked] = useState<Record<string, number>>({});
  const [condition, setCondition] = useState('');
  const [vision, setVision] = useState(view?.visibility?.vision[0]?.key ?? '');
  const [extraLabel, setExtraLabel] = useState('');
  const [extraValue, setExtraValue] = useState('0');
  const [poolDice, setPoolDice] = useState('0');
  const [typedWielder, setTypedWielder] = useState(melee && Boolean(view?.codes.wielder));
  // Astral combat (issue 1531): a melee attack between two creatures in astral states, where the system names them.
  const [astral, setAstral] = useState(false);
  const astralRules = melee ? systemRules.astral ?? null : null;
  const [myReach, setMyReach] = useState('0');
  const [theirReach, setTheirReach] = useState('0');
  const [way, setWay] = useState<'own' | 'other'>('own');
  const [theirTicked, setTheirTicked] = useState<Record<string, number>>({});
  const [theirVision, setTheirVision] = useState(view?.visibility?.vision[0]?.key ?? '');
  // The firing mode (issue 1529): the rounds when the mode lets the attacker choose, the recoil compensation, a heavy weapon.
  const [mode, setMode] = useState('');
  const [rounds, setRounds] = useState('');
  const [compensation, setCompensation] = useState('0');
  const [typedHeavy, setTypedHeavy] = useState(false);
  const woundRules = useWoundRules();

  const attacker = tokens.find((o) => o.id === who);
  const addsWielder = melee && Boolean(wielder) && typedWielder && readsWielder(attacker, answer);
  const targets = tokens.filter((o) => o.id !== who);
  const target = targets.find((o) => o.id === whom) ?? null;
  useEffect(() => {
    setAnswer(null); setWeapon(TYPED); setSkill('0');
    if (!attacker?.character && !attacker?.entry) return undefined;
    let live = true;
    setAnswer('loading');
    void fetchTests(campaignId, who, scene?.id ?? null).then((a) => {
      if (!live) return;
      setAnswer(a);
      // A melee-only row (the unarmed attack, a spirit's Engulf; issue 1172) is never the Ranged form's.
      const first = (a?.weapons ?? []).findIndex((w) => melee || !w.melee);
      if (first >= 0) setWeapon(`w:${first}`);
    });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [who, attacker?.character, attacker?.entry?.name, campaignId]);

  const got = answer && answer !== 'loading' ? answer : null;
  const skills = attackChoices(got);
  const row: SheetWeaponRow | null = weapon.startsWith('w:') ? got?.weapons?.[Number(weapon.slice(2))] ?? null : null;
  // The choices as shown (issue 1547): a sheet row only while the sheet has it, else the one typed in — a token with no
  // sheet, or a sheet with no skills, starts at the typed rating with its boxes.
  const skillShown = skill !== TYPED && skills[Number(skill)] ? skill : TYPED;
  const weaponShown = row ? weapon : TYPED;
  // The weapon's skill by its type, as the row changes.
  useEffect(() => { if (row) setSkill(String(skillFor(row.subtype, skills))); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [weapon, got]);
  // A typed weapon's Power: its own, or (melee) the wielder's rating plus it (`codes.wielder`), which the server reads from the sheet.
  const typedP = int(typedPower);
  const code: Omit<StagedWeapon, 'name'> | null = view ? (row ? parseCodeText(row.code, view.codes, kinds)
    : typedP !== null && (addsWielder ? typedP >= -STAGED_REQUEST_LIMITS.reach : typedP >= 1) && typedLevel
      ? { power: typedP, level: typedLevel, ...(typedKind && typedKind !== kinds[0]?.key ? { damageKind: typedKind } : {}), ...(addsWielder ? { wielder: true as const } : {}) } : null) : null;
  // The modes the weapon fires in, by the system's list (issue 1529): its register's, or every one for a weapon typed in.
  const firing = useMemo(() => (view?.firing && !melee ? parseFiring(view.firing) : null), [view, melee]);
  const modes = firing ? firing.modes.filter((m) => (row ? row.modes?.includes(m.key) ?? false : true)) : [];
  useEffect(() => {
    setMode(row?.modes?.length ? modes[0]?.key ?? '' : '');
    setRounds(''); setCompensation(String(row?.recoilComp?.base ?? 0)); setTypedHeavy(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weapon, got, firing]);
  const modeRule = modes.find((m) => m.key === mode) ?? null;
  const heavy = Boolean(firing?.heavy && (row ? row.subtype && firing.heavy.subtypes.includes(row.subtype) : typedHeavy));
  const comp = Math.max(0, Math.min(FIRING_LIMITS.compensation, int(compensation) ?? 0));
  const roundsN = modeRule ? fireRounds(modeRule, typeof modeRule.rounds === 'number' ? undefined : int(rounds) ?? undefined) : null;
  const chosenSkill = skillShown === TYPED ? null : skills[Number(skillShown)] ?? null;
  const rating = chosenSkill ? chosenSkill.rating : int(typedRating);
  const skillName = chosenSkill ? chosenSkill.name : typedSkill.trim();
  // The distance and the band it falls in, from the weapon's bands; the attacker may change it.
  const grid = store.state.grid as { x: { basePitch: number }; y: { basePitch: number } };
  const metres = attacker && target && systemRules.distance ? tokenDistance(attacker, target, grid, systemRules.distance.perSquare) : null;
  const found = view && metres !== null && !melee ? bandAt(metres, row?.bands, view.ranges) : null;
  useEffect(() => { if (found) setBand(found); else if (!band && view) setBand(view.ranges[0]?.key ?? ''); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [found, whom, weapon]);
  // Melee's base: the melee TN in place of a band.
  const bandRule = meleeRule ? { key: 'melee', label: 'Melee', target: meleeRule.target } : view?.ranges.find((b) => b.key === band) ?? null;
  // Each side's reach (melee): the weapon's and the natural, as this person is sent them; either may be changed.
  useEffect(() => { if (melee) setMyReach(String((row?.reach ?? 0) + naturalReach(attacker?.values))); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [weapon, who, got]);
  useEffect(() => { if (melee) setTheirReach(String(naturalReach(target?.values))); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [whom]);
  const reachA = Math.max(0, Math.min(STAGED_REQUEST_LIMITS.reach, int(myReach) ?? 0));
  const reachD = Math.max(0, Math.min(STAGED_REQUEST_LIMITS.reach, int(theirReach) ?? 0));
  const reachDiff = Math.abs(reachA - reachD);
  const longer = reachA > reachD ? 'attacker' : reachD > reachA ? 'defender' : null;
  const reachOnMe = longer && ((longer === 'attacker' && way === 'own') || (longer === 'defender' && way === 'other')) ? (way === 'own' ? -reachDiff : reachDiff) : 0;
  // The attacker's wound (from the numbers this person is sent) and the TN as the server will build it.
  const wound = attacker ? woundsOf(woundRules, attacker.values?.resources).target : 0;
  const situations = (view?.situations ?? []).filter((s) => s.only !== (melee ? 'ranged' : 'melee'));
  const sitTerms = situations.filter((s) => ticked[s.key]).map((s) => ({ label: s.label.toLowerCase(), value: s.target * (s.per === 'count' ? ticked[s.key]! : 1) }));
  const cond = view?.visibility?.conditions.find((c) => c.key === condition) ?? null;
  // In melee a condition counts at half, rounded down, unless it (or the table) says whole (Full darkness).
  const visMode = cond?.melee ?? view?.visibility?.melee;
  const visTerm = cond ? halfIn(melee, visMode, cond.target[vision] ?? 0) : 0;
  const extra = int(extraValue) ?? 0;
  const terms = [...sitTerms, ...(visTerm ? [{ label: cond!.label.toLowerCase(), value: visTerm }] : []), ...(extraLabel.trim() && extra ? [{ label: extraLabel.trim(), value: extra }] : []), ...(reachOnMe ? [{ label: 'reach', value: reachOnMe }] : []), ...(wound ? [{ label: 'wounded', value: wound }] : [])];
  // The attacker's pool dice: a pool added as dice drawn on for an attack, at most its cap (the skill) and what is left.
  const enc = store.state.encounter as { combatants?: Fighting[] } | undefined;
  const fighting = enc?.combatants?.find((c) => c.tokens.includes(who));
  // The recoil (issue 1529): the rounds already fired this phase (as the attacker's combatant carries them) and these.
  const levels = view?.codes.levels ?? [];
  const at = code ? levels.findIndex((l) => l.key === code.level) : -1;
  const fire = modeRule && roundsN !== null && code && at >= 0
    ? fireOf(modeRule, roundsN, firedNow(fighting, store.state.encounter as never), comp, heavy && firing?.heavy ? firing.heavy.times : 1, { power: code.power, level: code.level }, at, levels.length - 1) : null;
  const firedCode = fire && code ? { power: code.power + fire.power, abbr: levels[at + fire.levels]?.abbr ?? '' } : null;
  if (fire?.recoil) terms.splice(sitTerms.length + (visTerm ? 1 : 0), 0, { label: STAGED_RECOIL, value: fire.recoil });
  const tn = bandRule ? Math.max(view!.minTarget, bandRule.target + terms.reduce((n, t) => n + t.value, 0)) : null;
  // In astral combat the attacker draws on its state's pool (the Astral Combat Pool while projecting), else the melee pool.
  const statePool = astral && astralRules ? astralRules.states.find((x) => x.key === tokens.find((o) => o.id === who)?.astral)?.pool ?? null : null;
  const pool = (systemRules.pools ?? []).find((p) => (statePool ? p.key === statePool : addsDice(p) && drawsFor(p, 'attack')) && fighting?.pools?.[p.key]) ?? null;
  const left = pool ? fighting!.pools![pool.key]!.left : 0;
  const cap = pool && rating ? poolCap(pool, statePool && !drawsFor(pool, 'attack') ? 'astral' : 'attack', rating) : null;
  const most = Math.min(left, cap ?? left);
  const poolN = int(poolDice) ?? 0;
  const ready = Boolean(view && attacker && target && code && rating && rating >= 1 && skillName && bandRule && poolN >= 0 && poolN <= most && (row || typedName.trim()) && (!melee || meleeRule) && (!modeRule || roundsN !== null));
  // What is still to be given before Fire (or Attack) is pressed, named plainly (as the defence prompt does, issue 1548).
  const missing = [
    ...(!attacker ? ['an attacker'] : []), ...(!target ? ['a target'] : []),
    ...(!skillName ? ["the skill's name"] : []), ...(!(rating && rating >= 1) ? ["the skill's rating"] : []),
    ...(!(row || typedName.trim()) ? ["the weapon's name"] : []), ...(!code ? ["the weapon's Power"] : []),
    ...(!bandRule ? ['the range'] : []), ...(modeRule && roundsN === null ? ['how many rounds'] : []),
    ...(poolN < 0 || poolN > most ? [`at most ${most} pool dice`] : []),
  ];

  if (!view || (melee && !meleeRule)) return null;
  if (!mine.length) {
    return (
      <div className="rounded border border-stone-700 p-2 text-xs text-stone-400" data-testid="staged-form">
        You have no token at the table to attack with.
        <button type="button" className={`${btn} ml-2`} onClick={onClose}>Close</button>
      </div>
    );
  }
  return (
    <form className="space-y-1 rounded border border-rose-800 p-2 text-xs" data-testid={melee ? 'melee-form' : 'staged-form'} onSubmit={(e) => {
      e.preventDefault();
      if (!ready || !code || !rating || !bandRule) return;
      const body = {
        kind: 'attack.staged', attacker: who, target: whom,
        weapon: { name: (row?.name ?? typedName.trim()).slice(0, STAGED_REQUEST_LIMITS.name), ...code }, ...(row?.row ? { sheetRow: row.row } : {}),
        skill: { name: skillName.slice(0, STAGED_REQUEST_LIMITS.name), rating, ...(chosenSkill?.ref ? { ref: chosenSkill.ref } : {}) },
        ...(melee ? {
          melee: {
            ...(reachA || reachD ? { reach: { attacker: reachA, defender: reachD, way } } : {}),
            defender: {
              situations: situations.filter((s) => theirTicked[s.key]).map((s) => ({ key: s.key, ...(s.per === 'count' ? { count: theirTicked[s.key] } : {}) })),
              ...(cond ? { vision: theirVision } : {}), modifiers: [],
            },
          },
        } : { band: bandRule.key }),
        situations: situations.filter((s) => ticked[s.key]).map((s) => ({ key: s.key, ...(s.per === 'count' ? { count: ticked[s.key] } : {}) })),
        ...(cond ? { visibility: { condition: cond.key, vision } } : {}),
        modifiers: extraLabel.trim() && extra ? [{ label: extraLabel.trim().slice(0, STAGED_REQUEST_LIMITS.label), value: Math.max(-50, Math.min(50, extra)) }] : [],
        ...(pool && poolN > 0 ? { pool: { key: pool.key, dice: poolN } } : {}),
        ...(astral && astralRules ? { astral: true } : {}),
        private: isDm ? priv : false,
        ...(modeRule && roundsN !== null ? { fire: { mode: modeRule.key, ...(typeof modeRule.rounds === 'number' ? {} : { rounds: roundsN }), compensation: comp, ...(heavy ? { heavy: true } : {}) } } : {}),
      };
      act(body as never, (reply) => { if (!reply.ok) setNotice(reply.reason ?? 'That attack could not be made.'); else onClose(); });
    }}>
      <div className="flex flex-wrap items-center gap-1">
        <label className="flex items-center gap-1">With
          <select aria-label="Attacker" className={field} value={who} onChange={(e) => setWho(e.target.value)}>
            {mine.map((o) => <option key={o.id} value={o.id}>{tokenLabel(o, store.state.encounter) || 'Unnamed token'}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-1">at
          <select aria-label="Target" className={field} value={whom} onChange={(e) => setWhom(e.target.value)}>
            <option value="">Pick a target…</option>
            {targets.map((o) => <option key={o.id} value={o.id}>{tokenLabel(o, store.state.encounter) || 'Unnamed token'}</option>)}
          </select>
        </label>
      </div>
      {answer === 'loading' && <p className="text-stone-400">Reading the character's sheet…</p>}
      {got?.notice && <p className="text-amber-300">{got.notice}</p>}
      <label className="flex items-center gap-1">Weapon
        <select aria-label="Weapon" className={field} value={weaponShown} onChange={(e) => setWeapon(e.target.value)}>
          {(got?.weapons ?? []).map((w, i) => (melee || !w.melee ? <option key={`w:${i}`} value={`w:${i}`}>{w.name} {w.code}</option> : null))}
          <option value={TYPED}>A weapon typed in…</option>
        </select>
      </label>
      {row?.note && <p className="text-stone-400" data-testid="staged-weapon-note">{row.name}: {row.note}</p>}
      {weaponShown === TYPED && (
        <div className="flex flex-wrap items-center gap-1" data-testid="staged-typed-weapon">
          <input aria-label="Weapon name" placeholder="Heavy pistol" maxLength={STAGED_REQUEST_LIMITS.name} className={`${field} w-28`} value={typedName} onChange={(e) => setTypedName(e.target.value)} />
          {melee && wielder && readsWielder(attacker, answer) && (
            <label className="flex items-center gap-1" title={`The Power is the wielder's ${wielder} plus this, read from the sheet.`}>
              <input type="checkbox" aria-label={`${wielder} +`} checked={typedWielder} onChange={(e) => setTypedWielder(e.target.checked)} />{wielder} +
            </label>
          )}
          <input aria-label="Power" type="number" min={addsWielder ? -STAGED_REQUEST_LIMITS.reach : 1} max={STAGED_REQUEST_LIMITS.power} className={`${field} w-12`} value={typedPower} onChange={(e) => setTypedPower(e.target.value)} />
          <select aria-label="Damage level" className={field} value={typedLevel} onChange={(e) => setTypedLevel(e.target.value)}>
            {view.codes.levels.map((l) => <option key={l.key} value={l.key}>{l.abbr} ({l.label})</option>)}
          </select>
          {kinds.length > 1 && (
            <select aria-label="Damage kind" className={field} value={typedKind} onChange={(e) => setTypedKind(e.target.value)}>
              {kinds.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
            </select>
          )}
        </div>
      )}
      <label className="flex items-center gap-1">Skill
        <select aria-label="Skill" className={field} value={skillShown} onChange={(e) => setSkill(e.target.value)}>
          {skills.map((c, i) => <option key={i} value={String(i)}>{c.name} {c.rating}</option>)}
          <option value={TYPED}>A rating typed in…</option>
        </select>
      </label>
      {skillShown === TYPED && (
        <div className="flex items-center gap-1">
          <input aria-label="Skill name" placeholder="Pistols" maxLength={STAGED_REQUEST_LIMITS.name} className={`${field} w-24`} value={typedSkill} onChange={(e) => setTypedSkill(e.target.value)} />
          <input aria-label="Skill rating" type="number" min={1} max={STAGED_REQUEST_LIMITS.rating} className={`${field} w-12`} value={typedRating} onChange={(e) => setTypedRating(e.target.value)} />
        </div>
      )}
      {melee && (
        <div className="flex flex-wrap items-center gap-1" data-testid="melee-reach">
          <label className="flex items-center gap-1">Reach, yours
            <input aria-label="Your reach" type="number" min={0} max={STAGED_REQUEST_LIMITS.reach} className={`${field} w-10`} value={myReach} onChange={(e) => setMyReach(e.target.value)} />
          </label>
          <label className="flex items-center gap-1">theirs
            <input aria-label="Their reach" type="number" min={0} max={STAGED_REQUEST_LIMITS.reach} className={`${field} w-10`} value={theirReach} onChange={(e) => setTheirReach(e.target.value)} />
          </label>
          {longer && (
            <select aria-label="Longer reach counts" className={field} value={way} onChange={(e) => setWay(e.target.value as 'own' | 'other')}>
              <option value="own">−{reachDiff} on the longer reach's own TN</option>
              <option value="other">+{reachDiff} on the other's TN</option>
            </select>
          )}
        </div>
      )}
      {firing && (row ? Boolean(modes.length) : true) && (
        <div className="flex flex-wrap items-center gap-1" data-testid="staged-firing">
          <label className="flex items-center gap-1">Mode
            <select aria-label="Firing mode" className={field} value={mode} onChange={(e) => setMode(e.target.value)}>
              {!row && <option value="">None</option>}
              {modes.map((m) => <option key={m.key} value={m.key}>{m.label} ({m.key})</option>)}
            </select>
          </label>
          {modeRule && typeof modeRule.rounds !== 'number' && (
            <label className="flex items-center gap-1">rounds
              <input aria-label="Rounds" type="number" min={modeRule.rounds.least} max={modeRule.rounds.most} placeholder={String(modeRule.rounds.least)} className={`${field} w-12`} value={rounds} onChange={(e) => setRounds(e.target.value)} />
            </label>
          )}
          {modeRule && (
            <label className="flex items-center gap-1" title={row?.recoilComp ? `The weapon's own: ${row.recoilComp.base}, ${row.recoilComp.full} with every integral accessory in use. Add any accessories' points.` : 'Points of recoil compensation, the weapon\'s and its accessories\'.'}>recoil compensation
              <input aria-label="Recoil compensation" type="number" min={0} max={FIRING_LIMITS.compensation} className={`${field} w-12`} value={compensation} onChange={(e) => setCompensation(e.target.value)} />
            </label>
          )}
          {modeRule && !row && firing?.heavy && (
            <label className="flex items-center gap-1"><input type="checkbox" aria-label="Heavy weapon" checked={typedHeavy} onChange={(e) => setTypedHeavy(e.target.checked)} />Heavy weapon (recoil ×{firing.heavy.times})</label>
          )}
          {fire && <span className="text-stone-400" data-testid="staged-recoil">{fire.prior ? `${fire.prior} recoil fired this phase; ` : ''}recoil {signed(fire.recoil)}{heavy && firing?.heavy ? ` (heavy, ×${firing.heavy.times})` : ''}</span>}
          {row?.ammo !== undefined && <span className="text-stone-400" data-testid="staged-ammo">Holds {row.ammo}; the table does not count rounds.</span>}
        </div>
      )}
      {!melee && <div className="flex flex-wrap items-center gap-1">
        <label className="flex items-center gap-1">Range
          <select aria-label="Range" className={field} value={band} onChange={(e) => setBand(e.target.value)}>
            {view.ranges.map((b) => <option key={b.key} value={b.key}>{b.label} (TN {b.target})</option>)}
          </select>
        </label>
        {metres !== null && <span className="text-stone-400" data-testid="staged-distance">{Math.round(metres * 10) / 10} {systemRules.distance?.unit}{row?.bands ? (found ? '' : ', out of the weapon\'s range') : ', the weapon\'s bands unknown'}</span>}
      </div>}
      <fieldset className="flex flex-wrap gap-x-2" data-testid="staged-situations">
        {situations.map((s) => (
          <label key={s.key} className="flex items-center gap-1">
            <input type="checkbox" aria-label={s.label} checked={Boolean(ticked[s.key])} onChange={(e) => setTicked((cur) => ({ ...cur, [s.key]: e.target.checked ? 1 : 0 }))} />
            {s.label} ({signed(s.target)})
            {s.per === 'count' && ticked[s.key] ? <input aria-label={`${s.label}: how many`} type="number" min={1} max={STAGED_REQUEST_LIMITS.count} className={`${field} w-10`} value={ticked[s.key]} onChange={(e) => setTicked((cur) => ({ ...cur, [s.key]: Math.max(1, Math.min(STAGED_REQUEST_LIMITS.count, int(e.target.value) ?? 1)) }))} /> : null}
          </label>
        ))}
      </fieldset>
      {melee && (
        <fieldset className="flex flex-wrap gap-x-2" data-testid="melee-their-situations">
          <legend className="text-stone-400">The defender's own</legend>
          {situations.map((s) => (
            <label key={s.key} className="flex items-center gap-1">
              <input type="checkbox" aria-label={`Defender: ${s.label}`} checked={Boolean(theirTicked[s.key])} onChange={(e) => setTheirTicked((cur) => ({ ...cur, [s.key]: e.target.checked ? 1 : 0 }))} />
              {s.label} ({signed(s.target)})
              {s.per === 'count' && theirTicked[s.key] ? <input aria-label={`Defender: ${s.label}: how many`} type="number" min={1} max={s.most ?? STAGED_REQUEST_LIMITS.count} className={`${field} w-10`} value={theirTicked[s.key]} onChange={(e) => setTheirTicked((cur) => ({ ...cur, [s.key]: Math.max(1, Math.min(s.most ?? STAGED_REQUEST_LIMITS.count, int(e.target.value) ?? 1)) }))} /> : null}
            </label>
          ))}
        </fieldset>
      )}
      {view.visibility && (
        <div className="flex flex-wrap items-center gap-1" data-testid="staged-visibility">
          <label className="flex items-center gap-1">Visibility
            <select aria-label="Visibility" className={field} value={condition} onChange={(e) => setCondition(e.target.value)}>
              <option value="">Clear</option>
              {view.visibility.conditions.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </label>
          {cond && (
            <select aria-label="Vision" className={field} value={vision} onChange={(e) => setVision(e.target.value)}>
              {view.visibility.vision.map((v) => <option key={v.key} value={v.key}>{v.label} ({signed(halfIn(melee, visMode, cond.target[v.key] ?? 0))})</option>)}
            </select>
          )}
          {cond && melee && (
            <select aria-label="Defender's vision" className={field} value={theirVision} onChange={(e) => setTheirVision(e.target.value)}>
              {view.visibility.vision.map((v) => <option key={v.key} value={v.key}>Defender: {v.label} ({signed(halfIn(melee, visMode, cond.target[v.key] ?? 0))})</option>)}
            </select>
          )}
        </div>
      )}
      <div className="flex items-center gap-1">
        <input aria-label="Other modifier label" placeholder="other" maxLength={STAGED_REQUEST_LIMITS.label} className={`${field} w-20`} value={extraLabel} onChange={(e) => setExtraLabel(e.target.value)} />
        <input aria-label="Other modifier to the TN" type="number" className={`${field} w-12`} value={extraValue} onChange={(e) => setExtraValue(e.target.value)} />
      </div>
      {pool && (
        <label className="flex items-center gap-1" data-testid="staged-pool">{pool.label} dice
          <input aria-label="Pool dice" type="number" min={0} max={most} className={`${field} w-12`} value={poolDice} onChange={(e) => setPoolDice(e.target.value)} />
          <span className="text-stone-400">at most {most} ({left} left{cap !== null ? `, the skill caps it at ${cap}` : ''})</span>
        </label>
      )}
      {astralRules && (
        <label className="flex items-center gap-1" data-testid="melee-astral" title="Both sides must be on the astral plane: each draws on its own state's pool, and worn armour may not count.">
          <input type="checkbox" aria-label="Astral combat" checked={astral} onChange={(e) => setAstral(e.target.checked)} /> Astral combat
        </label>
      )}
      {melee && <p className="text-stone-400">The defender answers with their own skill, weapon and pool dice; more successes hits, a tie goes to the attacker.</p>}
      {tn !== null && <p data-testid="staged-tn">TN {tn}{terms.length ? ` (${bandRule!.target} ${bandRule!.label.toLowerCase()}${terms.map((t) => ` ${signed(t.value)} ${t.label}`).join('')})` : ` (${bandRule!.label.toLowerCase()})`}{rating ? ` · ${rating}${poolN ? ` + ${poolN}` : ''} dice` : ''}{firedCode ? ` · ${firedCode.power}${firedCode.abbr}` : code ? ` · ${code.power}${view.codes.levels.find((l) => l.key === code.level)?.abbr ?? ''}` : ''}</p>}
      <div className="flex gap-1">
        <button type="submit" className={`${btn} border-rose-700`} disabled={!ready} title={ready ? undefined : `Give ${and(missing)} first.`}>{melee ? 'Attack' : 'Fire'}</button>
        <button type="button" className={btn} onClick={onClose}>Close</button>
        {isDm && <span className="self-center text-stone-400">{priv ? 'Private: players see only that they were attacked.' : 'Public: the table sees it.'}</span>}
      </div>
      {!ready && missing.length > 0 && <p className="text-stone-400" data-testid="staged-missing">{melee ? 'To attack' : 'To fire'}, give {and(missing)}.</p>}
    </form>
  );
}

/**
 * The defender's question (§5.5): how many pool dice for the dodge or the resistance, from 0 to what is left of the
 * defender's pool — `poolAsk`'s buttons — read from what this person is sent (their own combatant's pools, or the DM's).
 * The DM may also give the resistance's rating and armour, when the defender's sheet has none.
 */
export function StagedAsk({ rollId, token, step, question, poolKey, dm }: { rollId: string; token: string; step: 'dodge' | 'resist'; question: string; poolKey: string | null; dm?: boolean }) {
  const { store, act, setNotice, systemRules } = useTableState();
  const [sent, setSent] = useState(false);
  const [rating, setRating] = useState('');
  const [armour, setArmour] = useState('');
  const enc = store.state.encounter as { combatants?: Fighting[] } | undefined;
  const left = poolKey ? enc?.combatants?.find((c) => c.tokens.includes(token))?.pools?.[poolKey]?.left ?? 0 : 0;
  const rule = (systemRules.pools ?? []).find((p) => p.key === poolKey);
  const choices = useMemo(() => poolAsk(rule ?? { label: 'pool' }, step, left, null).choices, [rule, step, left]);
  const send = (dice: number) => {
    setSent(true);
    const r = int(rating); const a = int(armour);
    act({ kind: 'attack.answer', rollId, dice, ...(dm && step === 'resist' && r !== null && r >= 1 ? { rating: r } : {}), ...(dm && step === 'resist' && a !== null && a >= 0 ? { armour: a } : {}) } as never,
      (reply) => { if (!reply.ok) { setSent(false); setNotice(reply.reason ?? 'That could not be answered.'); } });
  };
  return (
    <div className="mt-1 flex flex-wrap items-center gap-1 font-sans" data-testid={`staged-ask-${rollId}`}>
      <span className="text-amber-300">{question}</span>
      {choices.map((c) => <button key={c} type="button" className={btn} disabled={sent} onClick={() => send(Number(c))}>{c}</button>)}
      {dm && step === 'resist' && (
        <>
          <input aria-label="Resistance rating" placeholder="rating" type="number" min={1} className={`${field} w-14`} value={rating} onChange={(e) => setRating(e.target.value)} title="Only when the defender's sheet has none." />
          <input aria-label="Armour" placeholder="armour" type="number" min={0} className={`${field} w-14`} value={armour} onChange={(e) => setArmour(e.target.value)} title="Only when the defender's sheet has none." />
        </>
      )}
    </div>
  );
}

/**
 * The defender's melee test (issue 1528; §5.6): their melee skill (their sheet's skill rows, or one typed), the weapon
 * they fight with (their sheet's weapon rows, or one typed, adding the wielder's rating if it does), and pool dice — 0 to what is left,
 * at most the skill. The DM answers so for a creature nobody plays.
 */
export function MeleeAsk({ rollId, token, question, poolKey }: { rollId: string; token: string; question: string; poolKey: string | null }) {
  const { store, act, setNotice, systemRules, scene } = useTableState();
  const { campaignId } = useShell();
  const view = systemRules.staged;
  const kinds = systemRules.damageKinds ?? [];
  const [sent, setSent] = useState(false);
  const [answer, setAnswer] = useState<TestsAnswer | 'loading'>(null);
  const [skill, setSkill] = useState(TYPED);
  const [typedSkill, setTypedSkill] = useState('');
  const [typedRating, setTypedRating] = useState('3');
  const [weapon, setWeapon] = useState(TYPED);
  const [typedName, setTypedName] = useState('');
  const [typedPower, setTypedPower] = useState('0');
  const [typedWielder, setTypedWielder] = useState(Boolean(view?.codes.wielder));
  const wielder = view?.codes.wielder?.label ?? null;
  const [typedLevel, setTypedLevel] = useState(view?.codes.levels[1]?.key ?? view?.codes.levels[0]?.key ?? '');
  const [typedKind, setTypedKind] = useState(kinds[0]?.key ?? '');
  const o = (store.state.objects as Record<string, TokenLike>)[token];
  useEffect(() => {
    if (!o?.character && !o?.entry) return undefined;
    let live = true;
    setAnswer('loading');
    void fetchTests(campaignId, token, scene?.id ?? null).then((a) => {
      if (!live) return;
      setAnswer(a);
      if (attackChoices(a).length) setSkill('0');
      if (a?.weapons?.length) setWeapon('w:0');
    });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, o?.character, o?.entry?.name, campaignId]);
  const got = answer && answer !== 'loading' ? answer : null;
  const skills = attackChoices(got);
  const addsWielder = Boolean(wielder) && typedWielder && readsWielder(o, answer);
  // The choices as shown (issue 1547): a sheet row only while the sheet has it, else the one typed in.
  const chosen = skill === TYPED ? null : skills[Number(skill)] ?? null;
  const skillShown = chosen ? skill : TYPED;
  const rating = chosen ? chosen.rating : int(typedRating);
  const skillName = chosen ? chosen.name : typedSkill.trim();
  const row = weapon.startsWith('w:') ? got?.weapons?.[Number(weapon.slice(2))] ?? null : null;
  const weaponShown = row ? weapon : TYPED;
  const typedP = int(typedPower);
  const code = view ? (row ? parseCodeText(row.code, view.codes, kinds)
    : typedP !== null && (addsWielder ? typedP >= -STAGED_REQUEST_LIMITS.reach : typedP >= 1) && typedLevel
      ? { power: typedP, level: typedLevel, ...(typedKind && typedKind !== kinds[0]?.key ? { damageKind: typedKind } : {}), ...(addsWielder ? { wielder: true as const } : {}) } : null) : null;
  const weaponName = (row?.name ?? typedName.trim()).slice(0, STAGED_REQUEST_LIMITS.name);
  const enc = store.state.encounter as { combatants?: Fighting[] } | undefined;
  const left = poolKey ? enc?.combatants?.find((c) => c.tokens.includes(token))?.pools?.[poolKey]?.left ?? 0 : 0;
  const rule = (systemRules.pools ?? []).find((p) => p.key === poolKey);
  // An astral pool (issue 1531) drawn on for astral combat alone is capped as one.
  const kind = rule && drawsFor(rule, 'astral') && !drawsFor(rule, 'attack') ? 'astral' as const : 'attack' as const;
  const cap = rule && rating ? poolCap(rule, kind, rating) : null;
  const choices = poolAsk(rule ?? { label: 'pool' }, kind, left, cap).choices;
  const ready = Boolean(code && rating && rating >= 1 && skillName && weaponName);
  // What is still to be typed before the pool buttons answer (issue 1548), named plainly.
  const missing = [
    ...(!skillName ? ["the skill's name"] : []), ...(!(rating && rating >= 1) ? ["the skill's rating"] : []),
    ...(!weaponName ? ["the weapon's name"] : []), ...(!code ? ["the weapon's Power"] : []),
  ];
  const send = (dice: number) => {
    if (!ready || !code || !rating) return;
    setSent(true);
    act({ kind: 'attack.answer', rollId, dice, melee: { skill: { name: skillName.slice(0, STAGED_REQUEST_LIMITS.name), rating, ...(chosen?.ref ? { ref: chosen.ref } : {}) }, weapon: { name: weaponName, ...code } } } as never,
      (reply) => { if (!reply.ok) { setSent(false); setNotice(reply.reason ?? 'That could not be answered.'); } });
  };
  if (!view) return null;
  return (
    <div className="mt-1 space-y-1 font-sans" data-testid={`melee-ask-${rollId}`}>
      <p className="text-amber-300">{question}</p>
      {answer === 'loading' && <p className="text-stone-400">Reading the character's sheet…</p>}
      <div className="flex flex-wrap items-center gap-1">
        <select aria-label="Melee skill" className={field} value={skillShown} onChange={(e) => setSkill(e.target.value)}>
          {skills.map((c, i) => <option key={i} value={String(i)}>{c.name} {c.rating}</option>)}
          <option value={TYPED}>A rating typed in…</option>
        </select>
        {skillShown === TYPED && (
          <>
            <input aria-label="Melee skill name" placeholder="Unarmed Combat" maxLength={STAGED_REQUEST_LIMITS.name} className={`${field} w-28`} value={typedSkill} onChange={(e) => setTypedSkill(e.target.value)} />
            <input aria-label="Melee skill rating" type="number" min={1} max={STAGED_REQUEST_LIMITS.rating} className={`${field} w-12`} value={typedRating} onChange={(e) => setTypedRating(e.target.value)} />
          </>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-1">
        <select aria-label="Melee weapon" className={field} value={weaponShown} onChange={(e) => setWeapon(e.target.value)}>
          {(got?.weapons ?? []).map((w, i) => <option key={`w:${i}`} value={`w:${i}`}>{w.name} {w.code}</option>)}
          <option value={TYPED}>A weapon typed in…</option>
        </select>
        {weaponShown === TYPED && (
          <>
            <input aria-label="Melee weapon name" placeholder="Unarmed" maxLength={STAGED_REQUEST_LIMITS.name} className={`${field} w-24`} value={typedName} onChange={(e) => setTypedName(e.target.value)} />
            {wielder && readsWielder(o, answer) && <label className="flex items-center gap-1" title={`The Power is the wielder's ${wielder} plus this, read from the sheet.`}><input type="checkbox" aria-label={`Melee weapon ${wielder} +`} checked={typedWielder} onChange={(e) => setTypedWielder(e.target.checked)} />{wielder} +</label>}
            <input aria-label="Melee weapon Power" type="number" className={`${field} w-12`} value={typedPower} onChange={(e) => setTypedPower(e.target.value)} />
            <select aria-label="Melee weapon level" className={field} value={typedLevel} onChange={(e) => setTypedLevel(e.target.value)}>
              {view.codes.levels.map((l) => <option key={l.key} value={l.key}>{l.abbr} ({l.label})</option>)}
            </select>
            {kinds.length > 1 && (
              <select aria-label="Melee weapon kind" className={field} value={typedKind} onChange={(e) => setTypedKind(e.target.value)}>
                {kinds.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
              </select>
            )}
          </>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-1">
        <span className="text-stone-400">{rule?.label ?? 'Pool'} dice:</span>
        {choices.map((c) => <button key={c} type="button" className={btn} disabled={sent || !ready} title={ready ? undefined : `Type ${and(missing)} first.`} onClick={() => send(Number(c))}>{c}</button>)}
      </div>
      {!ready && missing.length > 0 && <p className="text-stone-400" data-testid={`melee-ask-missing-${rollId}`}>Type {and(missing)} to answer.</p>}
    </div>
  );
}

/**
 * The DM's parts of a staged attack in the log: the question while it waits, whoever plays the side asked — the
 * defender's dodge, resistance or melee test, or (issue 1528) the attacker's resistance when the defender hit.
 */
export function StagedDmParts({ roll }: { roll: RollRecord }) {
  const f = roll.attack;
  const st = f?.staged;
  if (!f || !st?.ask) return null;
  if (st.ask === 'melee') return <MeleeAsk rollId={roll.id} token={f.target.id} question={`${f.target.name} — Defence: the melee skill, the weapon, and how many ${st.melee?.pool?.label ?? 'pool'} dice?`} poolKey={st.melee?.pool?.key ?? null} />;
  const side = stagedAsked(st) === 'attacker' ? f.attacker : f.target;
  const pool = st.ask === 'dodge' ? st.pools.dodge : st.pools.resist;
  return <StagedAsk rollId={roll.id} token={side.id} step={st.ask} question={`${side.name} — ${st.ask === 'dodge' ? 'Dodge' : 'Resistance'}: how many ${pool?.label ?? 'pool'} dice?`} poolKey={pool?.key ?? null} dm />;
}
